// ts-rest contract for the workflow builder (#333 slice 6).
//
// The presentation projection of the `Workflow` Object Type + its run / propose
// / provider-config surfaces (CONTEXT.md → "Client Application Architecture").
// All document, event, and agent-proposal shapes are reused verbatim from
// `@septima/workflow` — there is no second copy of those schemas here.
//
// Endpoints under `/api/workflows`:
//   GET    /api/workflows                 — summary list (no full graphs)
//   GET    /api/workflows/:id             — one full Workflow document (404 missing)
//   POST   /api/workflows                 — save (create-or-update → SaveWorkflow)
//   DELETE /api/workflows/:id             — delete (→ DeleteWorkflow)
//   POST   /api/workflows/:id/run         — kick a run; events stream over WS
//   POST   /api/workflows/propose         — AI builder proposal
//   GET    /api/workflows/provider-config — which providers are configured
//
// Document UPLOAD is intentionally NOT here — it is a bespoke multipart
// endpoint (`POST /api/workflows/documents`) outside ts-rest, mirroring the
// cash / reconciliation upload split.
//
// Run events are NOT carried by this contract: a kicked run streams its
// `ExecutionEvent`s over the authenticated WebSocket gateway at
// `/api/workflows/runs/ws` (slice 5). The `run` endpoint returns only the
// `runId` the client subscribes to with `{ type: 'subscribe-run', runId }`.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
    workflowSchema,
    workflowAgentConfigSchema,
    workflowAgentProposalRequestSchema,
    workflowAgentProposalResponseSchema,
} from '@septima/workflow';
import { workflowScheduleDtoSchema } from '../schemas/workflow-schedule';

const c = initContract();

// ---------------------------------------------------------------------------
// Response / request shapes
// ---------------------------------------------------------------------------

/** Summary row for the list view — never ships the full graph. */
export const workflowSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    createdAt: z.string(),
});
export type WorkflowSummary = z.infer<typeof workflowSummarySchema>;

export const listWorkflowsResponseSchema = z.object({
    workflows: z.array(workflowSummarySchema),
});
export type ListWorkflowsResponse = z.infer<typeof listWorkflowsResponseSchema>;

export const getWorkflowResponseSchema = z.object({
    workflow: workflowSchema,
});
export type GetWorkflowResponse = z.infer<typeof getWorkflowResponseSchema>;

export const saveWorkflowRequestSchema = z.object({
    id: z.string().uuid().optional(),
    name: z.string().min(1),
    document: workflowSchema,
});
export type SaveWorkflowRequest = z.infer<typeof saveWorkflowRequestSchema>;

export const saveWorkflowResponseSchema = z.object({
    id: z.string(),
});
export type SaveWorkflowResponse = z.infer<typeof saveWorkflowResponseSchema>;

export const deleteWorkflowResponseSchema = z.object({
    ok: z.literal(true),
});
export type DeleteWorkflowResponse = z.infer<typeof deleteWorkflowResponseSchema>;

export const runWorkflowResponseSchema = z.object({
    runId: z.string(),
});
export type RunWorkflowResponse = z.infer<typeof runWorkflowResponseSchema>;

/**
 * GET /workflows/:id/schedule (WS-D) — the workflow's cron schedule, or null
 * when it is unscheduled. The cron is the compiled expression; `presetKey`
 * records the friendly preset that produced it (null for raw cron).
 */
export const getScheduleResponseSchema = z.object({
    schedule: workflowScheduleDtoSchema.nullable(),
});
export type GetScheduleResponse = z.infer<typeof getScheduleResponseSchema>;

/**
 * POST /workflows/:id/schedule (WS-D) — set the schedule. `presetKey` is the
 * friendly preset; `cron` is required only for the `raw` preset (the server
 * compiles the pair to the stored cron via `compilePreset`).
 */
export const setScheduleRequestSchema = z.object({
    presetKey: z.string(),
    cron: z.string().optional(),
});
export type SetScheduleRequest = z.infer<typeof setScheduleRequestSchema>;

export const setScheduleResponseSchema = z.object({
    ok: z.literal(true),
});
export type SetScheduleResponse = z.infer<typeof setScheduleResponseSchema>;

export const clearScheduleResponseSchema = z.object({
    ok: z.literal(true),
});
export type ClearScheduleResponse = z.infer<typeof clearScheduleResponseSchema>;

/**
 * Node-options for the builder (WS-B): the persona-scoped Object Types an
 * `ontology-source` node may read, and the named-agent functions an
 * `agent-function` node may call. Both lists are derived per-request from the
 * caller's ctx (the Object Type list is persona-filtered).
 *
 * `name` / `group` / `advanced` curate the picker (Increment 0): a friendly
 * Title-Case name, a domain bucket to `<optgroup>` under, and a flag that
 * demotes raw/intermediate types (e.g. `cash-account-row`) behind an
 * "Advanced" disclosure so the user lands on the curated type
 * (`cash-position` → "Cash Balances") instead.
 */
export const nodeOptionsObjectTypeSchema = z.object({
    key: z.string(),
    summary: z.string(),
    name: z.string(),
    group: z.string(),
    advanced: z.boolean(),
});
export type NodeOptionsObjectType = z.infer<typeof nodeOptionsObjectTypeSchema>;

export const nodeOptionsAgentFunctionSchema = z.object({
    agentCode: z.string(),
    functionKey: z.string(),
    label: z.string(),
    description: z.string(),
});
export type NodeOptionsAgentFunction = z.infer<typeof nodeOptionsAgentFunctionSchema>;

export const nodeOptionsResponseSchema = z.object({
    objectTypes: z.array(nodeOptionsObjectTypeSchema),
    agentFunctions: z.array(nodeOptionsAgentFunctionSchema),
});
export type NodeOptionsResponse = z.infer<typeof nodeOptionsResponseSchema>;

import { errorBodyWithCode as errorBody } from '../schemas/common';

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const workflowsContract = c.router(
    {
        // NOTE on ordering: ts-rest/nest registers Express routes in this
        // object's key order, and Express matches first-wins. The STATIC-path
        // routes (`/workflows/provider-config`, `/workflows/propose`) MUST be
        // declared BEFORE the `:id` param routes (`/workflows/:id`,
        // `/workflows/:id/run`) or the param route shadows them — e.g. a GET to
        // `/workflows/provider-config` would otherwise bind `id='provider-config'`.
        /**
         * GET /api/workflows — summary list of saved workflows, newest first.
         * Returns `{ id, name, createdAt }` only; the full graph is fetched
         * per workflow via `get`.
         */
        list: {
            method: 'GET',
            path: '/workflows',
            responses: {
                200: listWorkflowsResponseSchema,
            },
            summary: 'List saved workflows (summary only).',
        },
        /**
         * GET /api/workflows/provider-config — which agent providers are
         * available (Claude / Ollama), their default models, and the default
         * provider to preselect in the builder. Declared before `get` so the
         * static path is not shadowed by the `:id` route.
         */
        providerConfig: {
            method: 'GET',
            path: '/workflows/provider-config',
            responses: {
                200: workflowAgentConfigSchema,
            },
            summary: 'Report available agent providers + default models.',
        },
        /**
         * GET /api/workflows/node-options — the builder's data-source +
         * agent-function options, scoped to the caller's persona. Declared
         * before the `:id` route so the static path is not shadowed (a GET to
         * `/workflows/node-options` would otherwise bind `id='node-options'`).
         */
        nodeOptions: {
            method: 'GET',
            path: '/workflows/node-options',
            responses: {
                200: nodeOptionsResponseSchema,
            },
            summary: 'List ontology data sources + agent functions for the builder.',
        },
        /**
         * POST /api/workflows/propose — ask the AI builder for a proposed
         * replacement workflow. 400 when the chosen provider is unconfigured
         * or the model output is irreparably invalid. Declared before the
         * `:id` POST routes so the static path is not shadowed.
         */
        propose: {
            method: 'POST',
            path: '/workflows/propose',
            body: workflowAgentProposalRequestSchema,
            responses: {
                200: workflowAgentProposalResponseSchema,
                400: errorBody,
            },
            summary: 'Propose a workflow from a natural-language prompt (AI builder).',
        },
        /**
         * POST /api/workflows — create-or-update a workflow (maps to the
         * SaveWorkflow Action). Omit `id` to create; supply it to update.
         */
        save: {
            method: 'POST',
            path: '/workflows',
            body: saveWorkflowRequestSchema,
            responses: {
                200: saveWorkflowResponseSchema,
                400: errorBody,
            },
            summary: 'Save (create or update) a workflow (registry-dispatched).',
        },
        /**
         * GET /api/workflows/:id — one full Workflow document. 404 when the id
         * is not a saved workflow.
         */
        get: {
            method: 'GET',
            path: '/workflows/:id',
            pathParams: z.object({ id: z.string() }),
            responses: {
                200: getWorkflowResponseSchema,
                404: errorBody,
            },
            summary: 'Get one saved workflow document.',
        },
        /**
         * DELETE /api/workflows/:id — hard-delete a workflow (maps to the
         * DeleteWorkflow Action; idempotent).
         */
        delete: {
            method: 'DELETE',
            path: '/workflows/:id',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: deleteWorkflowResponseSchema,
            },
            summary: 'Delete a workflow (registry-dispatched).',
        },
        /**
         * POST /api/workflows/:id/run — kick a run. Returns immediately with
         * the `runId`; the run's `ExecutionEvent`s stream over the WS gateway
         * at `/api/workflows/runs/ws` (subscribe with `{ type: 'subscribe-run',
         * runId }`). 404 when the workflow id is unknown.
         */
        run: {
            method: 'POST',
            path: '/workflows/:id/run',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: runWorkflowResponseSchema,
                404: errorBody,
            },
            summary: 'Kick a workflow run; events stream over the WS gateway.',
        },
        /**
         * GET /api/workflows/:id/schedule — the workflow's cron schedule, or
         * `{ schedule: null }` when unscheduled. 404 when the workflow id is
         * unknown / not owned by the caller.
         */
        getSchedule: {
            method: 'GET',
            path: '/workflows/:id/schedule',
            pathParams: z.object({ id: z.string() }),
            responses: {
                200: getScheduleResponseSchema,
                404: errorBody,
            },
            summary: 'Get a workflow cron schedule (null when unscheduled).',
        },
        /**
         * POST /api/workflows/:id/schedule — set (create-or-update) the cron
         * schedule (maps to SetWorkflowSchedule). 400 on a bad cron / preset;
         * 404 when the workflow id is unknown / not owned.
         */
        setSchedule: {
            method: 'POST',
            path: '/workflows/:id/schedule',
            pathParams: z.object({ id: z.string() }),
            body: setScheduleRequestSchema,
            responses: {
                200: setScheduleResponseSchema,
                400: errorBody,
                404: errorBody,
            },
            summary: 'Set (create or update) a workflow cron schedule.',
        },
        /**
         * DELETE /api/workflows/:id/schedule — clear the cron schedule (maps to
         * ClearWorkflowSchedule; idempotent).
         */
        clearSchedule: {
            method: 'DELETE',
            path: '/workflows/:id/schedule',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: clearScheduleResponseSchema,
            },
            summary: 'Clear a workflow cron schedule (registry-dispatched).',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
